import { useAtomValue } from 'jotai';
import { ChevronDown, ExternalLink, Loader2, RotateCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { userAtom } from '@/atoms/user.atom';
import { formatDateTime } from '@/components/BatchList';
import { JobParamsPanel } from '@/components/JobParamsPanel';
import { Button } from '@/components/ui/button';
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getPlaceholderAvatar } from '@/core/avatar';
import { ProcessJobStatus } from '@/graphql/graphql';
import { comparisonRoles } from '@/lib/analyses/compare/runRoles';
import { isDeleteWarned, mergedRunBatchIds } from '@/lib/analyses/mergeRuns';
import { getAnalysis } from '@/lib/analyses/registry';
import { runDurationMs, runElapsedMs, toDate, type AnalysisRunFileItem } from '@/lib/analyses/runList';
import { isRunActive } from '@/lib/analyses/runs';
import { translateKey } from '@/lib/analyses/translateKey';
import { useAnalysisRunsDetails, useAnalysisRunsParams } from '@/lib/analyses/useAnalysisRunDetails';
import { useDeleteAnalysisRun } from '@/lib/analyses/useDeleteAnalysisRun';
import { isInternalUser } from '@/lib/email';

import type { DrawerBodyProps } from './AnalysisRunDetailsDrawer';
import { useFormatDuration } from './AnalysisRunRow';
import { ComparisonRerunDropdown } from './ComparisonRerunMenu';
import {
  DeleteRunButton,
  ErrorBox,
  Field,
  FileList,
  InputList,
  muted,
  PARAMS_COLLAPSE_AFTER,
  Section,
  StepLine,
  useDrawerClock,
} from './runDetailsParts';
import { runPartsOf, usePartLabel } from './RunPartTags';
import { RunStatusIcon } from './RunStatusIcon';

const subHeading = 'text-sm font-medium text-slate-900 dark:text-stone-100';

/**
 * The details drawer of a paired comparison (compare-surfaces spec §4.3):
 * one header and summary for the comparison, then each result — cut & fill
 * first — with its own status, batch, result layer or failure, parameters and
 * steps; the files of both, a shared report once. Re-run is the comparison's
 * menu (both / one part), Delete removes both batches.
 */
export function ComparisonRunDetails({
  projectId,
  href,
  row,
  nowMs,
  files,
  filesByRun,
  layersById,
  canRerun,
  onClose,
  rerunChoices,
  onRerunChoice,
  onOpenLayer,
  onDownload,
}: DrawerBodyProps) {
  const { t, i18n } = useTranslation('project');
  const { t: tComponents } = useTranslation('components');
  const formatDuration = useFormatDuration();
  const partLabel = usePartLabel();
  const user = useAtomValue(userAtom);
  const { run, id, title } = row;
  const Icon = getAnalysis(id).icon;
  const isStaff = isInternalUser(user?.email);
  const parts = runPartsOf(run);

  const active = isRunActive(run, nowMs);
  const drawerNow = useDrawerClock(active);
  const now = Math.max(drawerNow, nowMs);
  const batches = useAnalysisRunsDetails(parts.map((p) => p.run), true);
  const params = useAnalysisRunsParams(parts.map((p) => p.run.id), true);
  const startedBy = batches[0]?.data?.createdBy;
  const inputOf = (part: 'cutFill' | 'deviation') => {
    const i = parts.findIndex((p) => p.part === part);
    return i >= 0 ? params[i]?.data : undefined;
  };
  const roles = comparisonRoles(inputOf('cutFill'), inputOf('deviation'));

  const { canDelete, isDeleting, confirmDelete } = useDeleteAnalysisRun(projectId, onClose);
  const batchIds = mergedRunBatchIds(run);

  const absolute = (value: unknown) =>
    toDate(value).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  const relative = (value: unknown) =>
    formatDateTime(toDate(value), (k, o) => translateKey(tComponents, k, o), now);
  const statusOf = (status: string) => translateKey(t, `analysisLauncher.runs.status.${status}`);

  const tookMs = active ? runElapsedMs(run, now) : runDurationMs(run);
  // A file of one part says which; one linked to both (the combined report)
  // needs no tag.
  const tagOf = (file: AnalysisRunFileItem) => {
    const owners = parts.filter(({ run: p }) => {
      const own = filesByRun?.get(p.id);
      return !!own && [...own.pdfs, ...own.others].some((f) => f.id === file.id);
    });
    return owners.length === 1 ? partLabel(owners[0].part) : null;
  };

  return (
    <>
      <SheetHeader className='border-b border-slate-200/70 pr-10 dark:border-stone-800'>
        <div className='flex items-center gap-2 text-xs text-slate-500 dark:text-stone-400'>
          <Icon className='size-4 shrink-0' aria-hidden />
          <span>{title}</span>
        </div>
        <SheetTitle data-testid='analysis-run-details-title' className='break-words text-base'>
          {run.name || title}
        </SheetTitle>
        <SheetDescription asChild>
          <div
            data-testid='analysis-run-details-status'
            data-status={run.displayStatus}
            className='flex items-center gap-1.5 text-sm [&>svg]:size-4'>
            <RunStatusIcon status={run.displayStatus} />
            {statusOf(run.displayStatus)}
          </div>
        </SheetDescription>
      </SheetHeader>

      <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4'>
        <dl data-testid='analysis-run-details-summary' className='grid grid-cols-2 gap-x-4 gap-y-3'>
          <Field label={t('analyses.details.startedBy')} testId='analysis-run-details-started-by'>
            {startedBy ? (
              <span className='flex items-center gap-1.5'>
                <img
                  src={startedBy.avatar?.url || getPlaceholderAvatar(startedBy.email || startedBy.id)}
                  alt=''
                  className='size-4 rounded-full'
                />
                <span className='truncate'>{startedBy.name}</span>
              </span>
            ) : (
              '—'
            )}
          </Field>
          <Field label={t('analyses.details.started')} testId='analysis-run-details-started'>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className='cursor-help outline-none focus-visible:underline'>
                  {absolute(run.createdAt)}
                </span>
              </TooltipTrigger>
              <TooltipContent>{relative(run.createdAt)}</TooltipContent>
            </Tooltip>
          </Field>
          <Field label={t('analyses.details.finished')} testId='analysis-run-details-finished'>
            {run.finishedAt ? absolute(run.finishedAt) : t('analyses.details.notFinished')}
          </Field>
          <Field label={t('analyses.details.duration')} testId='analysis-run-details-duration'>
            {tookMs === null ? '—' : formatDuration(tookMs)}
          </Field>
        </dl>

        <Section title={t('analyses.details.inputs')} testId='analysis-run-details-inputs'>
          {roles ? (
            // The compare mode, then each input under its role (B6) — the
            // same words the dialog used, never a bare list of layers.
            <dl data-testid='analysis-run-details-roles' className='grid grid-cols-1 gap-y-3'>
              <Field label={t('compareSurfaces.kind.label')} testId='analysis-run-details-kind'>
                {t(roles.kind === 'survey' ? 'compareSurfaces.kind.survey' : 'compareSurfaces.kind.design')}
              </Field>
              {(['a', 'b'] as const).map((slot) => (
                <div key={slot} className='flex min-w-0 flex-col'>
                  <dt className='text-xs text-slate-500 dark:text-stone-400'>{translateKey(t, roles[slot].labelKey)}</dt>
                  <dd data-testid={`analysis-run-details-role-${slot}`} className='min-w-0'>
                    <InputList
                      layerIds={roles[slot].layerId ? [roles[slot].layerId!] : []}
                      layersById={layersById}
                      onOpenLayer={onOpenLayer}
                    />
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <InputList layerIds={run.inputLayerIds} layersById={layersById} onOpenLayer={onOpenLayer} />
          )}
        </Section>

        <Section title={t('analyses.details.results')} testId='analysis-run-details-result'>
          <ul className='flex flex-col gap-2'>
            {parts.map(({ part, run: p }, i) => {
              const batch = batches[i]?.data;
              const resultLayer = p.resultLayerId ? layersById.get(p.resultLayerId) : undefined;
              return (
                <li
                  key={p.id}
                  data-testid={`analysis-run-details-part-${part}`}
                  data-status={p.status}
                  className='flex flex-col gap-1.5 rounded-md border border-slate-200/70 p-3 dark:border-stone-800'>
                  <div className='flex items-center gap-2'>
                    <h4 className={subHeading}>{partLabel(part)}</h4>
                    <span className='flex items-center gap-1 text-xs text-slate-600 dark:text-stone-400 [&>svg]:size-3.5'>
                      <RunStatusIcon status={p.status} />
                      {statusOf(p.status)}
                    </span>
                    {p.batchId && batch?.key && (
                      <Link
                        to={`${href}/datacenter?batch=${p.batchId}`}
                        aria-label={`${t('analyses.details.batch')} ${batch.key.toUpperCase()}`}
                        className='ml-auto text-xs text-blue-700 underline-offset-2 hover:underline dark:text-blue-300'>
                        {batch.key.toUpperCase()}
                      </Link>
                    )}
                  </div>
                  {resultLayer ? (
                    <div className='flex items-center justify-between gap-2'>
                      <span className='truncate text-sm text-slate-900 dark:text-stone-100' title={resultLayer.name}>
                        {resultLayer.name}
                      </span>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        data-testid={`analysis-run-details-open-result-${part}`}
                        onClick={() => onOpenLayer(resultLayer.id)}>
                        <ExternalLink className='size-4' />
                        <span className='ml-1'>{t('analyses.details.openInViewer')}</span>
                      </Button>
                    </div>
                  ) : p.status === ProcessJobStatus.Failed ? null : (
                    <p className={muted}>
                      {p.status === ProcessJobStatus.Processed
                        ? t('analysisLauncher.runs.noResult')
                        : t('analyses.details.noResultYet')}
                    </p>
                  )}
                  {p.status === ProcessJobStatus.Failed && p.error && (
                    <ErrorBox
                      error={p.error}
                      testId={`analysis-run-details-error-${part}`}
                      copyTestId={`analysis-run-details-copy-error-${part}`}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </Section>

        <Section title={t('analyses.details.parameters')} testId='analysis-run-details-params'>
          {parts.map(({ part, run: p }, i) => {
            const q = params[i];
            return (
              <div key={p.id} className='flex flex-col gap-1'>
                <h4 className={subHeading}>{partLabel(part)}</h4>
                {!q || q.isPending ? (
                  <Loader2 className='size-4 animate-spin text-slate-400' />
                ) : q.isError ? (
                  <p className={muted}>{t('analyses.details.parametersFailed')}</p>
                ) : (
                  <JobParamsPanel
                    inputJson={q.data}
                    type={p.type}
                    staffTools={isStaff}
                    collapseAfter={PARAMS_COLLAPSE_AFTER}
                    emptyText={t('analyses.details.noParameters')}
                  />
                )}
              </div>
            );
          })}
        </Section>

        <Section title={t('analyses.details.steps')} testId='analysis-run-details-steps'>
          {parts.map(({ part, run: p }, i) => {
            const batch = batches[i]?.data;
            return (
              <div key={p.id} className='flex flex-col gap-1'>
                <h4 className={subHeading}>{partLabel(part)}</h4>
                {!batch ? (
                  <Loader2 className='size-4 animate-spin text-slate-400' />
                ) : (
                  <ul className='flex flex-col gap-1.5'>
                    {batch.jobs.map((step) => (
                      <StepLine key={step.id} step={step} isStaff={isStaff} />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </Section>

        <Section title={t('analyses.details.files')} testId='analysis-run-details-files'>
          <FileList files={[...files.pdfs, ...files.others]} relative={relative} onDownload={onDownload} tagOf={tagOf} />
        </Section>
      </div>

      <SheetFooter className='flex-row flex-wrap justify-end border-t border-slate-200/70 dark:border-stone-800'>
        <ComparisonRerunDropdown choices={rerunChoices} onChoose={onRerunChoice}>
          <Button
            type='button'
            variant='outline'
            size='sm'
            data-testid='analysis-run-details-rerun'
            disabled={!canRerun || rerunChoices.length === 0}>
            <RotateCw className='size-4' />
            <span className='ml-1'>{t('analyses.rerun')}</span>
            <ChevronDown className='ml-0.5 size-3.5' aria-hidden />
          </Button>
        </ComparisonRerunDropdown>
        {batchIds.length > 0 && canDelete && (
          <DeleteRunButton
            label={t('analyses.details.deleteComparison')}
            isDeleting={isDeleting}
            onClick={() => confirmDelete(batchIds, { active: isDeleteWarned(run) })}
          />
        )}
      </SheetFooter>
    </>
  );
}
